import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Clock, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Human-readable label + colour for every event the backend emits.
const EVENT_META = {
  quote_requested:                 { label: 'Quote requested',          dot: 'bg-gray-400' },
  quote_sent:                      { label: 'Quote sent',               dot: 'bg-blue-500' },
  quote_resent:                    { label: 'Quote re-sent',            dot: 'bg-blue-500' },
  quote_accepted:                  { label: 'Quote accepted',           dot: 'bg-emerald-600' },
  quote_declined:                  { label: 'Quote declined',           dot: 'bg-red-500' },
  booking_confirmed:               { label: 'Booking confirmed',        dot: 'bg-emerald-700' },
  calendar_event_created:          { label: 'Calendar event created',   dot: 'bg-emerald-600' },
  calendar_event_failed:           { label: 'Calendar event failed',    dot: 'bg-amber-500' },
  admin_confirmation_email_queued: { label: 'Internal team alerted',    dot: 'bg-gray-500' },
  driver_assigned:                 { label: 'Driver assigned',          dot: 'bg-purple-600' },
  driver_reassigned:               { label: 'Driver re-assigned',       dot: 'bg-purple-600' },
  driver_on_the_way:               { label: 'Driver tapped: On the way', dot: 'bg-blue-600' },
  driver_arrived:                  { label: 'Driver tapped: Arrived',   dot: 'bg-indigo-600' },
  driver_pob:                      { label: 'Driver tapped: Passenger on board', dot: 'bg-violet-600' },
  driver_completed:                { label: 'Driver tapped: Completed', dot: 'bg-emerald-700' },
  booking_completed:               { label: 'Job closed',               dot: 'bg-black' },
  booking_cancelled:               { label: 'Booking cancelled',        dot: 'bg-red-600' },
  booking_edited:                  { label: 'Booking edited',           dot: 'bg-gray-500' },
  email_on_the_way_sent:           { label: 'Customer "On the way" email sent', dot: 'bg-blue-500' },
  email_on_the_way_failed:         { label: '"On the way" email failed', dot: 'bg-amber-500' },
  sms_on_the_way_sent:             { label: 'Customer SMS "On the way" sent', dot: 'bg-cyan-500' },
  sms_on_the_way_failed:           { label: 'SMS "On the way" failed',  dot: 'bg-amber-500' },
  sms_arrived_sent:                { label: 'Customer SMS "Arrived" sent', dot: 'bg-cyan-500' },
  sms_arrived_failed:              { label: 'SMS "Arrived" failed',     dot: 'bg-amber-500' },
  reminder_email_sent:             { label: '24-hour reminder sent',    dot: 'bg-blue-400' },
  confirmation_email_resent:       { label: 'Confirmation resent',      dot: 'bg-blue-400' },
  invoice_emailed:                 { label: 'Invoice emailed',          dot: 'bg-emerald-500' },
  invoice_email_failed:            { label: 'Invoice email failed',     dot: 'bg-amber-500' },
  receipt_submitted:               { label: 'Driver receipt submitted', dot: 'bg-teal-500' },
  receipt_updated:                 { label: 'Receipt corrected by admin', dot: 'bg-teal-600' },
  receipt_deleted:                 { label: 'Receipt deleted by admin', dot: 'bg-red-500' },
  email_sent:                      { label: 'Email sent',               dot: 'bg-blue-400' },
  email_delivered:                 { label: 'Email delivered',          dot: 'bg-emerald-500' },
  email_opened:                    { label: 'Email opened',             dot: 'bg-emerald-600' },
  email_clicked:                   { label: 'Email link clicked',       dot: 'bg-emerald-700' },
  email_soft_bounced:              { label: 'Email soft-bounced',       dot: 'bg-amber-500' },
  email_bounced:                   { label: 'Email bounced',            dot: 'bg-red-600' },
  email_blocked:                   { label: 'Email blocked',            dot: 'bg-red-500' },
  email_spam:                      { label: 'Marked as spam',           dot: 'bg-red-500' },
  email_deferred:                  { label: 'Email deferred',           dot: 'bg-amber-400' },
  email_error:                     { label: 'Email error',              dot: 'bg-red-500' },
  email_invalid:                   { label: 'Invalid email address',    dot: 'bg-red-500' },
};

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  // Always display in UK time (GMT/BST) regardless of the viewer's timezone.
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  return `${parts.day}/${parts.month}/${parts.year}  ·  ${parts.hour}:${parts.minute}:${parts.second}`;
}

function actorLabel(actor) {
  if (!actor) return '';
  if (actor === 'system') return 'System';
  if (actor === 'admin') return 'Admin';
  if (actor === 'customer') return 'Customer';
  if (actor.startsWith('driver:')) return `Driver`;
  return actor;
}

export const JobLogTimeline = ({ bookingId, compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/admin/bookings/${bookingId}/log`, { withCredentials: true });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (bookingId) load();
    return () => { cancelled = true; };
  }, [bookingId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-6" data-testid="job-log-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading job log…
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-start gap-2 text-sm text-red-600 py-4" data-testid="job-log-error">
        <AlertCircle className="h-4 w-4 mt-0.5" /> <span>{String(error)}</span>
      </div>
    );
  }
  if (!data?.history?.length) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-6" data-testid="job-log-empty">
        <Clock className="h-4 w-4" /> No events yet for this booking.
      </div>
    );
  }

  const items = compact ? data.history.slice(0, 6) : data.history;

  return (
    <div className="relative" data-testid="job-log-timeline">
      {/* Vertical hairline */}
      <div className="absolute left-[7px] top-2 bottom-2 w-px bg-gray-200" aria-hidden="true" />
      <ul className="space-y-4">
        {items.map((e, idx) => {
          const meta = EVENT_META[e.event] || { label: e.event.replaceAll('_', ' '), dot: 'bg-gray-400' };
          return (
            <li key={idx} className="relative pl-8" data-testid={`job-log-item-${idx}`}>
              <span className={`absolute left-[2px] top-1.5 h-3 w-3 rounded-full ring-2 ring-white ${meta.dot}`} />
              <div className="flex items-baseline justify-between gap-4">
                <p className="text-sm font-semibold text-black">{meta.label}</p>
                <p className="text-[11px] text-gray-500 tabular-nums whitespace-nowrap">{fmtTime(e.at)}</p>
              </div>
              <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 mt-0.5">{actorLabel(e.actor)}</p>
              {e.meta && Object.keys(e.meta).length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
                  {Object.entries(e.meta).map(([k, v]) => (
                    <span key={k} className="text-[11px] text-gray-600">
                      <span className="text-gray-400">{k}:</span> {String(v)}
                    </span>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {compact && data.history.length > items.length && (
        <p className="mt-4 text-xs text-gray-500" data-testid="job-log-more-count">
          + {data.history.length - items.length} more event{data.history.length - items.length === 1 ? '' : 's'}
        </p>
      )}
    </div>
  );
};

export default JobLogTimeline;
